import {
  softValidationRuleFields,
  type SoftValidationWarning,
  type SuperFormDefinition,
  type WarningDismissal,
} from '@mes-amis/super-form';
import { HybridLogicalClockService } from './crdt/hlc';
import { compareTimestamps } from './crdt/document-crdt';

/** An assessor's decision to keep an unlikely combination of answers (a soft
 *  validation warning), as stored on the local document and exchanged with
 *  the offline API (sent as `document[warning_dismissals_attributes]`). Merge
 *  lists with `mergeWarningDismissals`. */
export interface WarningDismissalRecord {
  key: string;
  dismissed_by?: string | null;
  justification?: string | null;
  undone?: boolean;
  /** HLC stamp of the last dismiss or undo. */
  changed_hlc?: string | null;
  /** Changed on this device and not yet accepted by the server. Local only. */
  pending?: boolean;
}

/** Whether a stamp can be ordered (a well-formed HLC string). */
function isStamp(stamp: string | null | undefined): boolean {
  if (!stamp) return false;
  try {
    HybridLogicalClockService.fromString(stamp);
    return true;
  } catch {
    return false;
  }
}

const sameDismissal = (a: WarningDismissalRecord, b: WarningDismissalRecord) =>
  a.changed_hlc === b.changed_hlc && a.undone === b.undone && a.justification === b.justification
  && a.dismissed_by === b.dismissed_by && a.pending === b.pending;

/**
 * Merges two lists per key: the newer `changed_hlc` wins, ordered like the
 * answers (`compareTimestamps`), so arrival order doesn't matter and a late,
 * older copy never overwrites a newer undo. On the same stamp (the same write)
 * the `incoming` copy wins — the server's, with its `dismissed_by` — unless it
 * is still pending and the local one is not. Keys only one side has are kept.
 * Returns `local` itself when nothing changed.
 */
export function mergeWarningDismissals<T extends WarningDismissalRecord>(
  local: readonly T[] | null | undefined,
  incoming: readonly T[] | null | undefined,
): T[] {
  const merged = new Map((local ?? []).map((item) => [item.key, item]));
  let changed = false;
  for (const item of incoming ?? []) {
    const current = merged.get(item.key);
    if (current) {
      // Positive: the copy already here is newer. Zero: the same write.
      const order = compareTimestamps(current.changed_hlc, item.changed_hlc);
      if (order > 0 || sameDismissal(item, current)) continue;
      if (order === 0 && item.pending && !current.pending) continue;
    }
    merged.set(item.key, item);
    changed = true;
  }
  return changed ? Array.from(merged.values()) : ((local ?? []) as T[]);
}

/** Whether a dismissal still applies, as the server's `WarningDismissal#covers?`:
 *  not undone, and none of the rule's questions stamped after it. A stamp that
 *  can't be ordered re-opens nothing. */
export function dismissalApplies(
  record: WarningDismissalRecord,
  fields: readonly string[],
  fieldStamps: Readonly<Record<string, string>> | null | undefined,
): boolean {
  if (record.undone) return false;
  if (!isStamp(record.changed_hlc)) return true;
  // A missing or malformed answer stamp orders before the dismissal.
  return fields.every((slug) => compareTimestamps(fieldStamps?.[slug], record.changed_hlc) <= 0);
}

/** What the offline API accepts for one dismissal. */
export type WarningDismissalUpload = Pick<WarningDismissalRecord, 'key' | 'justification' | 'undone' | 'changed_hlc'>;

/** A server list (download): every item is already accepted. */
export function serverWarningDismissals(list: unknown): WarningDismissalRecord[] | undefined {
  if (!Array.isArray(list)) return undefined;
  return list
    .filter((item): item is WarningDismissalRecord => !!item && typeof item === 'object' && typeof (item as { key?: unknown }).key === 'string')
    .map(({ pending: _pending, ...item }) => item);
}

/** The items to send with the next update / finalize: those changed here since the last successful sync. */
export function pendingWarningDismissals(list: readonly WarningDismissalRecord[] | null | undefined): WarningDismissalUpload[] {
  return (list ?? [])
    .filter((item) => item.pending)
    .map(({ key, justification, undone, changed_hlc }) => ({
      key,
      justification: justification ?? null,
      undone: undone === true,
      changed_hlc: changed_hlc ?? null,
    }));
}

/**
 * After the server accepted an upload: settles the items it carried (unless
 * they changed again while it was in flight — a newer stamp stays pending),
 * then folds in the server's list when the response had one.
 */
export function acknowledgeWarningDismissals(
  current: readonly WarningDismissalRecord[] | null | undefined,
  sent: readonly WarningDismissalUpload[],
  serverList?: readonly WarningDismissalRecord[],
): WarningDismissalRecord[] {
  const sentStamps = new Map(sent.map((item) => [item.key, item.changed_hlc]));
  const settled = (current ?? []).map((item) => (
    item.pending && sentStamps.has(item.key) && sentStamps.get(item.key) === (item.changed_hlc ?? null)
      ? { ...item, pending: false }
      : item
  ));
  return serverList ? mergeWarningDismissals(settled, serverList) : settled;
}

/** Whether `incoming` carries anything `local` doesn't already have (a newer stamp, or a new key). */
export function hasNewerWarningDismissals(
  local: readonly WarningDismissalRecord[] | null | undefined,
  incoming: readonly WarningDismissalRecord[] | null | undefined,
): boolean {
  const byKey = new Map((local ?? []).map((item) => [item.key, item]));
  return (incoming ?? []).some((item) => {
    const current = byKey.get(item.key);
    return !current || compareTimestamps(current.changed_hlc, item.changed_hlc) < 0;
  });
}

/** The stamps a list carries, keyed for `advanceHLCFromRemoteTimestamps`. */
export function warningDismissalStamps(list: readonly WarningDismissalRecord[] | null | undefined): Record<string, string> {
  const stamps: Record<string, string> = {};
  for (const item of list ?? []) {
    if (item.changed_hlc) stamps[`warning_dismissal:${item.key}`] = item.changed_hlc;
  }
  return stamps;
}

/** The dismissals the editor shows: those that still apply against the document's
 *  `super_form_meta`, in the package's shape. One not yet synced shows the local
 *  person's name. One without a stamp is left out: the editor tells a new
 *  dismissal from the one an edit overtook only by its version. */
export function toEditorWarningDismissals(
  list: readonly WarningDismissalRecord[] | null | undefined,
  localPersonName: string | undefined,
  definition: SuperFormDefinition | undefined,
  fieldStamps: Readonly<Record<string, string>> | null | undefined,
): WarningDismissal[] {
  return (list ?? [])
    .filter((item): item is WarningDismissalRecord & { changed_hlc: string } => !!item.changed_hlc)
    .filter((item) => dismissalApplies(item, softValidationRuleFields(definition, item.key), fieldStamps))
    .map((item) => ({
      key: item.key,
      dismissedBy: item.dismissed_by || (item.pending ? localPersonName : undefined) || undefined,
      justification: item.justification ?? undefined,
      version: item.changed_hlc,
    }));
}

/** Open warnings from a rejected finalize (its `warnings` key), or undefined when it reported none. */
export function finalizeWarnings(warnings: unknown): SoftValidationWarning[] | undefined {
  if (!Array.isArray(warnings)) return undefined;
  const valid = warnings.filter((w): w is SoftValidationWarning =>
    !!w && typeof w === 'object' && typeof (w as { key?: unknown }).key === 'string' && Array.isArray((w as { fields?: unknown }).fields));
  return valid.length > 0
    ? valid.map((w) => ({ ...w, label: w.label || w.fields.join(' vs ') }))
    : undefined;
}
